import { BBPage } from '@/components/BBPage'
import { HealthAlertRow } from '@/components/HealthAlerts'
import { Sparkline } from '@/components/charts'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useActivityFeed, type TvActivityItem } from '@/hooks/use-activity-feed'
import { useBusinessMetrics } from '@/hooks/use-business-metrics'
import { useHealthSummary, type HealthHeroDay } from '@/hooks/use-health-summary'
import { formatCurrency, formatDate, formatNumber, formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import { SIGNAL_STYLES, type SignalLevel } from '@/utils/signals'
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  Check,
  CircleDollarSign,
  CreditCard,
  HeartCrack,
  Home as HomeIcon,
  Megaphone,
  Repeat,
  Rocket,
  Settings,
  Trophy,
  UserPlus,
  Zap,
} from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

// ============================================
// Hero KPI cards (yesterday + 7d trend + signal)
// ============================================

interface KpiMetric {
  label: string
  icon: typeof UserPlus
  /** Yesterday's (or current) headline value, preformatted */
  value: string
  /** Context line under the value */
  sub: string
  /** Delta chip (vs prior-week average or 7d change), signal-colored */
  delta: { text: string; signal: SignalLevel } | null
  spark: Array<{ date: string | number; value: number }>
  sparkFormat?: (v: number) => string
}

/** Grade yesterday against the prior-week daily average (volume metrics). */
function volumeSignal(yesterday: number, priorAvg: number): SignalLevel {
  if (priorAvg <= 0) return yesterday > 0 ? 'excellent' : 'neutral'
  const ratio = yesterday / priorAvg
  if (ratio >= 1.25) return 'excellent'
  if (ratio >= 0.95) return 'good'
  if (ratio >= 0.7) return 'neutral'
  if (ratio >= 0.4) return 'warning'
  return 'poor'
}

/**
 * Honest headline label: 'yesterday' only when the last hero row actually IS
 * yesterday — a stale pipeline shows the real day instead, e.g. 'Jul 6 (latest)'.
 */
function latestDayLabel(days: HealthHeroDay[]): string {
  const latest = days[days.length - 1]?.date
  if (!latest) return 'yesterday'
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  return latest === yesterday
    ? 'yesterday'
    : `${formatDate(latest, { month: 'short', day: 'numeric' })} (latest)`
}

function heroMetric(
  days: HealthHeroDay[],
  key: 'signups' | 'trialsStarted' | 'paidConversions',
  label: string,
  icon: typeof UserPlus
): KpiMetric {
  const values = days.map(d => d[key])
  const yesterday = values[values.length - 1] ?? 0
  const last7 = values.slice(-7)
  const total7 = last7.reduce((a, b) => a + b, 0)
  const prior = values.slice(0, -1)
  const priorAvg = prior.length > 0 ? prior.reduce((a, b) => a + b, 0) / prior.length : 0
  const deltaPct = priorAvg > 0 ? Math.round(((yesterday - priorAvg) / priorAvg) * 100) : null
  return {
    label,
    icon,
    value: formatNumber(yesterday),
    sub: `${latestDayLabel(days)} · ${formatNumber(total7)} in 7d`,
    delta:
      deltaPct != null
        ? {
            text: `${deltaPct >= 0 ? '+' : ''}${deltaPct}%`,
            signal: volumeSignal(yesterday, priorAvg),
          }
        : null,
    spark: days.map(d => ({ date: d.date, value: d[key] })),
  }
}

function spendMetric(days: HealthHeroDay[]): KpiMetric {
  const yesterday = days[days.length - 1]?.adSpend ?? null
  const last7 = days.slice(-7).map(d => d.adSpend)
  const total7 = last7.reduce<number>((a, b) => a + (b ?? 0), 0)
  const anySpend = last7.some(v => v != null)
  return {
    label: 'Ad Spend',
    icon: Megaphone,
    value: yesterday != null ? formatCurrency(yesterday) : '—',
    sub: `${latestDayLabel(days)} · ${anySpend ? formatCurrency(total7, { compact: true }) : '—'} in 7d`,
    // Spend has no good/bad direction — always neutral.
    delta: null,
    spark: days.map(d => ({ date: d.date, value: d.adSpend ?? 0 })),
    sparkFormat: v => formatCurrency(v),
  }
}

const formatSparkDate = (label: string | number) =>
  formatDate(label, { month: 'short', day: 'numeric' })

function KpiCard({ metric }: { metric: KpiMetric }) {
  const Icon = metric.icon
  const style = metric.delta ? SIGNAL_STYLES[metric.delta.signal] : null
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {metric.label}
          </p>
          <Icon className="h-3.5 w-3.5 text-muted-foreground/60" />
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <p className="text-xl font-bold text-foreground sm:text-2xl">{metric.value}</p>
          {metric.delta && style && (
            <span className={cn('rounded px-1 py-0.5 text-[10px]', style.text, style.bg)}>
              {metric.delta.text}
            </span>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">{metric.sub}</p>
        <div className="mt-2">
          <Sparkline
            data={metric.spark}
            dataKey="value"
            xKey="date"
            label={metric.label}
            height={36}
            valueFormat={metric.sparkFormat ?? (v => formatNumber(v))}
            labelFormat={formatSparkDate}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function KpiErrorCard({ label, message }: { label: string; message: string }) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
        </div>
        <p className="mt-1 text-sm font-medium text-destructive">Unavailable</p>
        <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  )
}

function KpiSkeletonCard() {
  return (
    <Card>
      <CardContent className="space-y-2 p-3 sm:p-4">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-7 w-20" />
        <Skeleton className="h-9 w-full" />
      </CardContent>
    </Card>
  )
}

function HeroRow() {
  const summary = useHealthSummary()
  const business = useBusinessMetrics()

  const heroDays = summary.data?.hero ?? null
  const funnelMetrics = useMemo(() => {
    if (!heroDays || heroDays.length === 0) return null
    return [
      heroMetric(heroDays, 'signups', 'Signups', UserPlus),
      heroMetric(heroDays, 'trialsStarted', 'Trials Started', Rocket),
      heroMetric(heroDays, 'paidConversions', 'Paid Conversions', CircleDollarSign),
    ]
  }, [heroDays])

  const subsMetric = useMemo<KpiMetric | null>(() => {
    const data = business.data
    if (!data) return null
    const trend = data.activeSubscriptionsTrend ?? []
    const weekAgo = trend.length >= 8 ? trend[trend.length - 8] : null
    const delta7 = weekAgo != null ? data.activeSubscriptions - weekAgo : null
    const now = Date.now()
    return {
      label: 'Active Subs',
      icon: CreditCard,
      value: formatNumber(data.activeSubscriptions),
      sub: 'active now',
      delta:
        delta7 != null
          ? {
              text: `${delta7 >= 0 ? '+' : ''}${delta7} in 7d`,
              signal: delta7 > 0 ? 'good' : delta7 === 0 ? 'neutral' : 'poor',
            }
          : null,
      spark: trend.slice(-7).map((value, i, arr) => ({
        date: now - (arr.length - 1 - i) * 24 * 60 * 60 * 1000,
        value,
      })),
    }
  }, [business.data])

  const funnelError = summary.error
    ? summary.error instanceof Error
      ? summary.error.message
      : String(summary.error)
    : !summary.loading && !funnelMetrics
      ? 'Daily funnel metrics source is failing — see System Health.'
      : null

  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-5">
      {summary.loading ? (
        <>
          <KpiSkeletonCard />
          <KpiSkeletonCard />
          <KpiSkeletonCard />
        </>
      ) : funnelError ? (
        <>
          <KpiErrorCard label="Signups" message={funnelError} />
          <KpiErrorCard label="Trials Started" message={funnelError} />
          <KpiErrorCard label="Paid Conversions" message={funnelError} />
        </>
      ) : (
        funnelMetrics?.map(m => <KpiCard key={m.label} metric={m} />)
      )}
      {business.loading ? (
        <KpiSkeletonCard />
      ) : business.error || !subsMetric ? (
        <KpiErrorCard
          label="Active Subs"
          message={
            business.error instanceof Error ? business.error.message : 'Business metrics failed'
          }
        />
      ) : (
        <KpiCard metric={subsMetric} />
      )}
      {summary.loading ? (
        <KpiSkeletonCard />
      ) : funnelError || !heroDays ? (
        <KpiErrorCard label="Ad Spend" message={funnelError ?? 'No data'} />
      ) : (
        <KpiCard metric={spendMetric(heroDays)} />
      )}
    </div>
  )
}

// ============================================
// Alerts strip (hidden when all green)
// ============================================

function AlertsStrip() {
  const { data } = useHealthSummary()
  if (!data || data.alerts.length === 0) return null
  return (
    <div className="space-y-1.5">
      {data.alerts.map(alert => (
        <HealthAlertRow key={alert.id} alert={alert} />
      ))}
    </div>
  )
}

// ============================================
// Live activity strip (Taka TV feed)
// ============================================

const EVENT_META: Record<
  TvActivityItem['event'],
  { icon: typeof Rocket; label: string; text: string }
> = {
  paid: {
    icon: CircleDollarSign,
    label: 'is paying',
    text: 'text-emerald-600 dark:text-emerald-400',
  },
  trial: { icon: Rocket, label: 'started a trial', text: 'text-amber-600 dark:text-amber-400' },
  renewed: { icon: Repeat, label: 'renewed', text: 'text-sky-600 dark:text-sky-400' },
  churned: { icon: HeartCrack, label: 'churned', text: 'text-red-600 dark:text-red-400' },
  topup: { icon: Zap, label: 'topped up', text: 'text-purple-600 dark:text-purple-400' },
}

function ActivityChip({ item }: { item: TvActivityItem }) {
  // The feed is a live external payload — an event type this build doesn't
  // know about must not crash the whole Home page.
  const meta = EVENT_META[item.event]
  if (!meta) return null
  const Icon = meta.icon
  return (
    <div className="flex shrink-0 items-center gap-2 rounded-lg border border-border/60 bg-card px-2.5 py-1.5">
      <Icon className={cn('h-3.5 w-3.5 shrink-0', meta.text)} />
      <span className="max-w-[140px] truncate text-xs font-medium text-foreground">
        {item.name}
      </span>
      <span className={cn('text-[11px]', meta.text)}>
        {meta.label}
        {item.amount != null && (
          <>
            {' '}
            {formatCurrency(item.amount)}
            {item.cadence === 'yearly' ? '/yr' : item.cadence === 'monthly' ? '/mo' : ''}
          </>
        )}
      </span>
      <span className="text-[10px] text-muted-foreground">{formatRelative(item.timestamp)}</span>
    </div>
  )
}

function ActivityStrip() {
  const { items, error } = useActivityFeed({ pollInterval: 60 * 1000 })
  return (
    <Card>
      <CardContent className="space-y-2 p-3 sm:p-4">
        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
          Live activity
        </div>
        {error && items.length === 0 ? (
          <p className="text-xs text-destructive">Activity feed unavailable: {error.message}</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground">Waiting for activity…</p>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {items.slice(0, 20).map(item => (
              <ActivityChip key={item.id} item={item} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// Milestones (compact single-row footer)
// ============================================

const milestoneDefinitions = [
  { id: 'launch', title: 'Launch', threshold: 0, hardcodedDate: 'Aug 2025' },
  { id: 'first-paying', title: 'First Paying User', threshold: 1, hardcodedDate: 'Sep 2025' },
  { id: '10-users', title: '10 Paying Users', threshold: 10, hardcodedDate: 'Nov 2025' },
  { id: '50-users', title: '50 Paying Users', threshold: 50 },
  { id: '100-users', title: '100 Paying Users', threshold: 100 },
] as const

function findMilestoneDate(
  threshold: number,
  chartDates: string[],
  chartSubscriptions: number[]
): string | undefined {
  if (threshold === 0 && chartDates.length > 0) return chartDates[0]
  for (let i = 0; i < chartSubscriptions.length; i++) {
    if (chartSubscriptions[i] >= threshold) return chartDates[i]
  }
  return undefined
}

function MilestonesFooter() {
  const { data: businessMetrics, loading, error } = useBusinessMetrics()
  const payingUsers = businessMetrics?.activeSubscriptions ?? 0

  const milestones = useMemo(() => {
    const chartDates = businessMetrics?.chartDates ?? []
    const chartSubscriptions = businessMetrics?.chartActiveSubscriptions ?? []
    return milestoneDefinitions.map((milestone, index) => {
      const prevThreshold = milestoneDefinitions[index - 1]?.threshold ?? 0
      if (payingUsers >= milestone.threshold) {
        const date =
          'hardcodedDate' in milestone && milestone.hardcodedDate
            ? milestone.hardcodedDate
            : (() => {
                const d = findMilestoneDate(milestone.threshold, chartDates, chartSubscriptions)
                return d ? formatDate(d, { month: 'short', year: 'numeric' }) : undefined
              })()
        return { ...milestone, status: 'completed' as const, date }
      }
      if (payingUsers >= prevThreshold) {
        return { ...milestone, status: 'current' as const }
      }
      return { ...milestone, status: 'upcoming' as const }
    })
  }, [payingUsers, businessMetrics])

  const current = milestones.find(m => m.status === 'current')
  const currentIndex = current ? milestones.indexOf(current) : -1
  const prevThreshold = currentIndex > 0 ? milestoneDefinitions[currentIndex - 1].threshold : 0
  const progressInMilestone =
    current && current.threshold > prevThreshold
      ? ((payingUsers - prevThreshold) / (current.threshold - prevThreshold)) * 100
      : 0
  const completedCount = milestones.filter(m => m.status === 'completed').length
  const gapPercent = 100 / (milestones.length - 1)
  const basePosition = completedCount > 0 ? (completedCount - 1) * gapPercent : 0
  const overallProgress = current ? basePosition + (progressInMilestone / 100) * gapPercent : 100

  if (error) return null

  return (
    <Card>
      <CardContent className="flex items-center gap-4 px-4 py-3">
        <div className="flex shrink-0 items-center gap-2">
          <Trophy className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">Milestones</span>
        </div>
        {loading ? (
          <Skeleton className="h-2 flex-1" />
        ) : (
          <>
            <div className="relative flex-1">
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-primary transition-all duration-700"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
              <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between">
                {milestones.map(m => (
                  <Tooltip key={m.id}>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          'flex h-4 w-4 cursor-help items-center justify-center rounded-full border-2 border-background',
                          m.status === 'completed' && 'bg-emerald-500 text-white',
                          m.status === 'current' && 'bg-primary ring-2 ring-primary/25',
                          m.status === 'upcoming' && 'bg-muted'
                        )}
                      >
                        {m.status === 'completed' && (
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {m.title}
                      {m.status === 'completed' && 'date' in m && m.date ? ` · ${m.date}` : ''}
                      {m.status === 'current' ? ` · ${payingUsers}/${m.threshold}` : ''}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">
              {current ? `${payingUsers}/${current.threshold} · ${current.title}` : 'All complete'}
            </span>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// Pillar navigation (demoted, compact)
// ============================================

const pillars = [
  {
    id: 'playbook',
    title: 'Playbook',
    description: "The source of truth for what we're building and why",
    icon: BookOpen,
    href: '/playbook',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'metrics',
    title: 'Metrics',
    description: 'Analyze funnel, business, marketing, and product',
    icon: BarChart3,
    href: '/metrics/business',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  {
    id: 'platform',
    title: 'Platform',
    description: 'Manage users, agents, and system settings',
    icon: Settings,
    href: '/platform/users',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
]

function PillarCard({ pillar }: { pillar: (typeof pillars)[0] }) {
  return (
    <Link to={pillar.href} className="group flex-1">
      <Card className="h-full transition-colors hover:border-primary/40">
        <CardContent className="flex items-center gap-3 p-4">
          <div className={cn('shrink-0 rounded-lg p-2', pillar.bgColor)}>
            <pillar.icon className={cn('h-5 w-5', pillar.color)} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
              {pillar.title}
            </p>
            <p className="truncate text-xs text-muted-foreground">{pillar.description}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

// ============================================
// Page
// ============================================

function Home() {
  const summary = useHealthSummary()
  const business = useBusinessMetrics()

  return (
    <BBPage
      title="BirdBrain"
      description="Taka's daily cockpit — yesterday's numbers, live activity, system health"
      icon={HomeIcon}
      refreshing={summary.refreshing || business.refreshing}
      onRefresh={() => {
        summary.refetch()
        business.refetch()
      }}
      updatedAt={summary.updatedAt ?? business.updatedAt}
    >
      {/* 1. Today's numbers */}
      <HeroRow />

      {/* 2. Anything red in system health finds you here first */}
      <AlertsStrip />

      {/* 3. Live activity (Taka TV feed) */}
      <ActivityStrip />

      {/* 4. Milestones, demoted to a compact footer */}
      <MilestonesFooter />

      {/* 5. Pillar navigation, below the fold */}
      <div className="flex flex-col gap-3 md:flex-row">
        {pillars.map(pillar => (
          <PillarCard key={pillar.id} pillar={pillar} />
        ))}
      </div>
    </BBPage>
  )
}

export default Home
